import { SDKFunction, ScanType, SdkError, SdkType } from '../interface';
import {
    asyncLoadScript,
    assign,
    includes,
    isNil,
    parseNumber,
    isMobileMiniProgramUA,
    isMiniProgramUA,
    insertURLQuery,
    isEmpty
} from '@libs/shared';
import { IMAGE_SUPPORT_TYPE, UPLOAD_FILE_COUNT, JSSDK_URL, SdkResponseCode } from '../constant';
import JsSdkBase from '../core/js-sdk-base';
import { jsSdkAuth, sdkLog, jsSdkFallback } from '../decorator';
import { processImageName, sdkLogger } from '../utils';

/**
 * 微信SDK
 * @see https://developers.weixin.qq.com/doc/service/guide/h5/jssdk.html
 */
export default class WeChatSdk extends JsSdkBase {
    constructor() {
        super();
        this.sdkType = SdkType.WeChat;
    }

    /**
     * 加载微信sdk脚本
     */
    @sdkLog()
    loadScript() {
        return asyncLoadScript(JSSDK_URL.WeChat);
    }

    @sdkLog()
    init(config: Parameters<JsSdkBase['init']>[0]) {
        /**
         * 如果logger处于debug模式，则开启微信sdk自带的debug模式
         * 注意，logger首次开启debug模式后，需要刷新页面后才能让微信sdk的debug模式生效
         * 因为 jsSdkConfig 是异步获取的，且会在 init 过程中被赋值，所以这里选择拦截
         */
        return super.init({
            fetchConfig: (url?: string) => {
                return config.fetchConfig(url).then((res) => {
                    const { jsSdkConfig, wxAgentJsSdkConfig } = res;

                    if (!isEmpty(jsSdkConfig) && sdkLogger.isDebug) {
                        assign(jsSdkConfig, {
                            debug: true
                        });
                    }
                    return { jsSdkConfig, wxAgentJsSdkConfig };
                });
            }
        });
    }

    /**
     * 注入权限验证配置
     * @see https://developers.weixin.qq.com/doc/service/guide/h5/jssdk.html#%E6%AD%A5%E9%AA%A4%E4%B8%89%EF%BC%9A%E9%80%9A%E8%BF%87config%E6%8E%A5%E5%8F%A3%E6%B3%A8%E5%85%A5%E6%9D%83%E9%99%90%E9%AA%8C%E8%AF%81%E9%85%8D%E7%BD%AE
     */
    @sdkLog()
    _config(config: JsSdkBase['jsSdkConfig']) {
        window.wx?.config(config);
    }

    _ready(callback: () => void) {
        window.wx?.ready(() => {
            sdkLogger.info(`${this.sdkType} ready callback`);
            callback();
        });
    }

    _error(callback: (err: any) => void) {
        window.wx?.error((err: any) => {
            sdkLogger.error(`${this.sdkType} error callback`, { err });
            callback(err);
        });
    }

    /**
     * 预览图片
     * @see https://developers.weixin.qq.com/doc/service/guide/h5/jssdk.html#%E9%A2%84%E8%A7%88%E5%9B%BE%E7%89%87%E6%8E%A5%E5%8F%A3
     * @see https://developers.weixin.qq.com/miniprogram/dev/api/media/image/wx.previewImage.html
     * @param urls 需要预览的图片链接列表
     * @param current 当前显示图片的链接
     */
    @jsSdkAuth
    @sdkLog()
    previewImage(urls: string[], current: string) {
        return new Promise((resolve, reject) => {
            window.wx?.previewImage({
                current: current, // 当前显示图片的http链接
                urls: urls, // 需要预览的图片http链接列表
                success: (result: any) => {
                    resolve(result);
                },
                fail: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        });
    }

    /**
     * 定位
     * @see https://developers.weixin.qq.com/doc/service/guide/h5/jssdk.html#%E8%8E%B7%E5%8F%96%E5%9C%B0%E7%90%86%E4%BD%8D%E7%BD%AE%E6%8E%A5%E5%8F%A3
     * @see https://developers.weixin.qq.com/miniprogram/dev/api/location/wx.getLocation.html
     */
    @jsSdkAuth
    @jsSdkFallback((originalArgs) => [{ ...originalArgs[0], noIpLocate: 0 }])
    @sdkLog()
    getLocation(): ReturnType<JsSdkBase['getLocation']> {
        return new Promise((resolve, reject) => {
            window.wx?.getLocation({
                type: 'gcj02',
                success: (result: any) => {
                    const lng = parseNumber(result.longitude);
                    const lat = parseNumber(result.latitude);
                    if (!isNil(lng) && !isNil(lat)) {
                        const position = [lng, lat];
                        resolve([position, result]);
                    } else {
                        reject(
                            new SdkError(
                                this.sdkType,
                                SdkResponseCode.Location.LngOrLatEmpty,
                                `longitude or latitude is empty, longitude: ${lng}, latitude: ${lat}`
                            )
                        );
                    }
                },
                fail: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                },
                /**
                 * 微信定位向用户请求权限若选择拒绝，会触发cancel回调，而不是fail回调
                 * @see https://code.fineres.com/projects/FX/repos/fx-code-mobile/pull-requests/5722/overview
                 */
                cancel: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        });
    }

    /**
     * 扫码
     * @see https://developer.work.weixin.qq.com/document/path/90525#%E6%97%A7%E7%89%88jweixin%E8%B0%83%E7%94%A8
     * @see https://developers.weixin.qq.com/doc/service/guide/h5/jssdk.html#%E5%BE%AE%E4%BF%A1%E6%89%AB%E4%B8%80%E6%89%AB
     * @see https://developers.weixin.qq.com/miniprogram/dev/api/device/scan/wx.scanCode.html (看着是新版，参数不大一样）
     * @param options 扫码参数
     */
    @jsSdkAuth
    @sdkLog()
    scanCode(options: Parameters<JsSdkBase['scanCode']>[0]) {
        let scanType = ['qrCode', 'barCode'];
        switch (options.type) {
            case ScanType.QrCode:
                scanType = ['qrCode'];
                break;
            case ScanType.BarCode:
                scanType = ['barCode'];
                break;
        }
        return new Promise((resolve, reject) => {
            window.wx?.scanQRCode({
                needResult: 1, // 默认为0，扫描结果由微信处理，1则直接返回扫描结果
                scanType, // 可以指定扫二维码还是一维码
                success: (res: any) => {
                    let result = res.resultStr;
                    if (includes(['barCode', 'all'], options.type)) {
                        // 一维码去除前缀（一维码类型）
                        result = result.replace(
                            /^(CODABAR|AZTEC|CODE_(25|39|93|128)|EAN_(8|13)|ITF|MAXICODE|RSS_(14|EXPANDED)|UPC_(EAN_EXTENSION|A|E)),/,
                            ''
                        );
                    }
                    resolve(result);
                },
                fail: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                },
                cancel: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        });
    }

    /**
     * 微信上传图片
     * @see https://developers.weixin.qq.com/doc/service/guide/h5/jssdk.html#%E4%B8%8A%E4%BC%A0%E5%9B%BE%E7%89%87%E6%8E%A5%E5%8F%A3
     * @param localId 图片ID
     */
    @jsSdkAuth
    @sdkLog()
    uploadImage(localId: string): Promise<string> {
        return new Promise((resolve, reject) => {
            window.wx?.uploadImage({
                localId: localId, // 需要上传的图片的本地ID，由chooseImage接口获得
                isShowProgressTips: 1, // 默认为1，显示进度提示
                success: (res: any) => {
                    const serverId = res.serverId; // 返回图片的服务器端ID
                    resolve(serverId);
                },
                fail: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                },
                cancel: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        });
    }

    /**
     * 拍照或从手机相册中选图
     * @see https://developers.weixin.qq.com/miniprogram/dev/api/media/image/wx.chooseImage.html
     * @see https://developers.weixin.qq.com/doc/service/guide/h5/jssdk.html#%E6%8B%8D%E7%85%A7%E6%88%96%E4%BB%8E%E6%89%8B%E6%9C%BA%E7%9B%B8%E5%86%8C%E4%B8%AD%E9%80%89%E5%9B%BE%E6%8E%A5%E5%8F%A3
     * @param options 配置参数
     */
    @jsSdkAuth
    @sdkLog()
    chooseImage(options: Parameters<JsSdkBase['chooseImage']>[0]) {
        const {
            compressed = false,
            onlyCamera = false,
            maxFileCount = UPLOAD_FILE_COUNT.SINGLE
        } = options;

        return new Promise<File[]>((resolve, reject) => {
            const sizeType = compressed ? ['compressed'] : ['original'];
            const sourceType = onlyCamera ? ['camera'] : ['album', 'camera'];
            const count = Math.min(maxFileCount, 9);
            window.wx?.chooseImage({
                count: count,
                sizeType: sizeType, // 可以指定是原图还是压缩图，默认二者都有
                sourceType: sourceType, // 可以指定来源是相册还是相机，默认二者都有
                success: async (res: any) => {
                    if (!compressed) {
                        /**
                         * 使用串行上传，否则微信可能上传失败
                         * 返回选定照片的本地ID列表，localId可以作为img标签的src属性显示图片
                         */
                        const serverIds: string[] = [];
                        const localIds = res.localIds;
                        for (const localId of localIds) {
                            const serverId = await this.uploadImage(localId);
                            serverIds.push(serverId);
                        }
                        resolve(serverIds as unknown as File[]);
                    } else {
                        /**
                         * 读取本地图片文件
                         */
                        const promises: Promise<File>[] = [];
                        for (const localId of res.localIds) {
                            const promise = new Promise<File>((resolve, reject) => {
                                /**
                                 * 获取本地图片接口
                                 * @see https://developers.weixin.qq.com/doc/service/guide/h5/jssdk.html#%E8%8E%B7%E5%8F%96%E6%9C%AC%E5%9C%B0%E5%9B%BE%E7%89%87%E6%8E%A5%E5%8F%A3
                                 */
                                window.wx?.getLocalImgData({
                                    localId: localId, // 图片的localID
                                    compressionRatio: 0.3,
                                    success: (res: any) => {
                                        const localData = res.localData; // localData是图片的base64数据
                                        // base64 转 file
                                        let arr = localData.split(',');

                                        // 处理base64数据，确保有正确的MIME类型前缀 目前已知：安卓微信公众号环境下没有
                                        if (!localData.startsWith('data:')) {
                                            // 如果没有MIME类型前缀，添加默认的
                                            arr = [`data:image/jpeg;base64`, localData];
                                        }

                                        //  @see https://project.feishu.cn/b2rl2h/story/detail/6167478667
                                        // 优先根据 localId 后缀判断 originalType
                                        let originalType = '';
                                        const extMatch = (localId.match(/\.(jpg|jpeg|png|gif)$/i) ||
                                            [])[0];
                                        if (extMatch) {
                                            const typeMap: Record<string, string> = {
                                                '.jpeg': 'image/jpeg',
                                                '.jpg': 'image/jpeg',
                                                '.png': 'image/png',
                                                '.gif': 'image/gif'
                                            };
                                            originalType = typeMap[extMatch.toLowerCase()] || '';
                                        } else {
                                            const mimeMatch = arr[0].match(/:(.*?);/);
                                            // 如果无法匹配到MIME类型，使用默认值
                                            originalType = mimeMatch ? mimeMatch[1] : '';
                                        }

                                        // 如果无法匹配到MIME类型，使用默认值
                                        const mime = includes(IMAGE_SUPPORT_TYPE, originalType)
                                            ? originalType
                                            : 'image/jpeg';
                                        const bstr = atob(arr[1]);

                                        // 将二进制字符串转换为 Uint8Array
                                        const u8arr = new Uint8Array(bstr.length);
                                        for (let i = 0; i < bstr.length; i++) {
                                            u8arr[i] = bstr.charCodeAt(i);
                                        }

                                        // 创建 Blob 对象
                                        const blob = new Blob([u8arr], { type: mime });
                                        /**
                                         * localId ios和安卓存在差别，安卓：//xxx/xxx/1241231231.jpg ios:1241231231
                                         * 需要给ios增加正确的后缀
                                         */
                                        const rawFileName = localId.split('/').pop() || 'image';
                                        const fileName = processImageName(rawFileName, mime);

                                        // 创建 File 对象
                                        const file = new File([blob], fileName, { type: mime });

                                        resolve(file);
                                    },
                                    fail: (err: any) => {
                                        reject(
                                            new SdkError(
                                                this.sdkType,
                                                SdkResponseCode.ThirdPartyServiceFail,
                                                JSON.stringify(err)
                                            )
                                        );
                                    }
                                });
                            });
                            promises.push(promise);
                        }
                        const files = await Promise.all(promises);
                        resolve(files);
                    }
                },
                fail: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                },
                cancel: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        });
    }

    @jsSdkAuth
    @sdkLog()
    downloadPrintFile({
        url: downloadUrl,
        fileType
    }: {
        url: string;
        fileType?: string;
        fileName?: string;
    }) {
        // 移动端小程序下载打印文件 - 需跳转到小程序页面下载
        if (isMobileMiniProgramUA()) {
            const url = insertURLQuery('/pages/downloadFile/downloadFile', [
                {
                    key: 'fileType',
                    value: fileType
                },
                {
                    key: 'downloadUrl',
                    value: encodeURIComponent(downloadUrl)
                }
            ]);
            return this.navigateTo(url);
        } else {
            return this.downloadLink({ url: downloadUrl });
        }
    }

    /**
     * 保留当前页面，跳转到应用内的某个页面
     * @see https://developers.weixin.qq.com/miniprogram/dev/component/web-view.html#%E7%9B%B8%E5%85%B3%E6%8E%A5%E5%8F%A3-1
     * @see https://developers.weixin.qq.com/miniprogram/dev/api/route/wx.navigateTo.html
     * @param url 目标地址
     */
    @jsSdkAuth
    @sdkLog()
    navigateTo(url: string) {
        return new Promise((resolve, reject) => {
            if (isMiniProgramUA()) {
                window.wx?.miniProgram.navigateTo({
                    url,
                    success: (res: any) => {
                        resolve(res);
                    },
                    fail: (err: any) => {
                        reject(
                            new SdkError(
                                this.sdkType,
                                SdkResponseCode.ThirdPartyServiceFail,
                                JSON.stringify(err)
                            )
                        );
                    }
                });
            } else {
                window.wx?.navigateTo({
                    url,
                    success: (res: any) => {
                        resolve(res);
                    },
                    fail: (err: any) => {
                        reject(
                            new SdkError(
                                this.sdkType,
                                SdkResponseCode.ThirdPartyServiceFail,
                                JSON.stringify(err)
                            )
                        );
                    }
                });
            }
        });
    }

    /**
     * 使用微信内置地图查看位置
     * @see https://developers.weixin.qq.com/doc/service/guide/h5/jssdk.html#%E4%BD%BF%E7%94%A8%E5%BE%AE%E4%BF%A1%E5%86%85%E7%BD%AE%E5%9C%B0%E5%9B%BE%E6%9F%A5%E7%9C%8B%E4%BD%8D%E7%BD%AE%E6%8E%A5%E5%8F%A3
     * @see https://developers.weixin.qq.com/miniprogram/dev/api/location/wx.openLocation.html
     * @param params 地图参数
     */
    @jsSdkAuth
    @sdkLog()
    openMap(params: Parameters<JsSdkBase['openMap']>[0]) {
        const { lng, lat, address } = params;
        return new Promise((resolve, reject) => {
            window.wx?.openLocation({
                latitude: lat,
                longitude: lng,
                name: address,
                scale: 16,
                success: (res: any) => {
                    resolve(res);
                },
                fail: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        });
    }

    /**
     * 关闭当前网页窗口
     * @see https://developers.weixin.qq.com/doc/service/guide/h5/jssdk.html#%E5%85%B3%E9%97%AD%E5%BD%93%E5%89%8D%E7%BD%91%E9%A1%B5%E7%AA%97%E5%8F%A3%E6%8E%A5%E5%8F%A3
     */
    @jsSdkAuth
    @sdkLog()
    closeWindow() {
        return new Promise<void>((resolve, reject) => {
            window.wx?.closeWindow({
                success: (res: any) => {
                    resolve(res);
                },
                fail: (err: any) => {
                    reject(
                        new SdkError(
                            this.sdkType,
                            SdkResponseCode.ThirdPartyServiceFail,
                            JSON.stringify(err)
                        )
                    );
                }
            });
        });
    }

    hasFunction(functionName: SDKFunction): Promise<boolean> {
        const functionList = [SDKFunction.ChooseImage];
        return Promise.resolve(functionList.includes(functionName));
    }
}
